import React from 'react';
import { V2Plan } from '../../utilities/types';
import { formatCurrency } from '../../../../lib/currency';
import { ComponentProps, deps, inject, observer } from '../../../../lib/component';
import { ACCOUNT_PLANS_ROUTE, REGISTRATION_ROUTE } from '../../../../constants';
import { PlanCard } from './PlanV2';

interface SubscriptionPlansProps extends ComponentProps {
  plans: V2Plan[];
  isRegistration?: boolean;
}

export const SubscriptionPlans = inject(deps)(
  observer(
    ({
      plans = [],
      isRegistration = false,
      model: {
        user: { isSubscribedToLegacyPlan, currentV2Subscription, pendingV2Subscription, v2SubscriptionDiscount },
        router,
        subscription: { v2PlanIsUpdating },
      },
      controller: {
        analytics: { sendMixpanel },
        modal: { showPreUpgradeModal, showPreDowngradeModal, showResubscribeModal },
        subscription: { navigateToStripeSubscriptionV2 },
      },
    }: SubscriptionPlansProps) => {
      const handleUpgrade = (plan: V2Plan): void => {
        if (router.location.pathname === `${REGISTRATION_ROUTE}/plans`) {
          router.push(ACCOUNT_PLANS_ROUTE);
          return;
        }

        sendMixpanel('User clicks plan upgrade', {
          route: router.location.pathname,
          planName: plan.plan_name,
          price: formatCurrency(plan.price / 100, plan.currency_code),
        });

        showPreUpgradeModal(plan);
      };

      const handleDowngrade = (plan: V2Plan): void => {
        if (router.location.pathname === `${REGISTRATION_ROUTE}/plans`) {
          router.push(ACCOUNT_PLANS_ROUTE);
          return;
        }

        sendMixpanel('User clicks plan downgrade', {
          route: router.location.pathname,
          planName: plan.plan_name,
          price: formatCurrency(plan.price / 100, plan.currency_code),
        });

        showPreDowngradeModal(plan);
      };

      const handleSelectPlan = (plan: V2Plan): void => {
        sendMixpanel('User clicks select plan', {
          route: router.location.pathname,
          planName: plan.plan_name,
          price: formatCurrency(plan.price / 100, plan.currency_code),
        });

        navigateToStripeSubscriptionV2(plan.stripe_id);
      };

      const handleResumeCancelledPlan = (plan: V2Plan): void => {
        if (!currentV2Subscription) return;

        if (router.location.pathname === `${REGISTRATION_ROUTE}/plans`) {
          router.push(ACCOUNT_PLANS_ROUTE);
          return;
        }

        sendMixpanel('User clicks plan re-subscribe', {
          route: router.location.pathname,
          planName: plan.plan_name,
          price: formatCurrency(plan.price / 100, plan.currency_code),
        });

        showResubscribeModal();
      };

      if (plans.length === 0) return null;

      return (
        <>
          {plans.map((plan) => (
            <PlanCard
              key={plan.plan_name}
              plan={plan}
              allPlans={plans}
              isRegistration={isRegistration}
              currentSubscription={currentV2Subscription}
              pendingSubscription={pendingV2Subscription}
              isSubscribedToLegacyPlan={isSubscribedToLegacyPlan}
              handleDowngrade={handleDowngrade}
              handleSelectPlan={handleSelectPlan}
              handleUpgrade={handleUpgrade}
              handleResumeCancelledPlan={handleResumeCancelledPlan}
              subscriptionDiscount={v2SubscriptionDiscount}
              planLoadingState={v2PlanIsUpdating}
            />
          ))}
        </>
      );
    }
  )
);
